/**
 * Application Entry Point
 * Renders the main App component and initializes the application
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { config, validateEnvironment } from './config/environment';
import './index.css';

// Validate environment configuration
const envValidation = validateEnvironment();
if (!envValidation.isValid) {
  console.error('❌ Environment validation failed:');
  envValidation.errors.forEach(error => console.error(`  - ${error}`));

  if (config.environment.isProduction) {
    // In production, show user-friendly error
    document.body.innerHTML = `
      <div style="
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        font-family: system-ui, sans-serif;
        background: #f3f4f6;
        color: #374151;
        text-align: center;
        padding: 2rem;
      ">
        <div>
          <h1 style="color: #dc2626; margin-bottom: 1rem;">Configuration Error</h1>
          <p>The application is not properly configured. Please contact support.</p>
        </div>
      </div>
    `;
    throw new Error('Environment validation failed');
  }
}

// Log application startup information
if (config.environment.isDevelopment) {
  console.log('🚀 Starting Poultry Management System');
  console.log('📋 Environment:', config.app.environment);
  console.log('🔧 Version:', config.app.version);
  console.log('🌐 API Base URL:', config.api.baseUrl);
  console.log('🎛️ Feature Flags:', config.features);
}

// Performance monitoring
const startTime = performance.now();

// Error handling for unhandled promise rejections
window.addEventListener('unhandledrejection', event => {
  console.error('Unhandled promise rejection:', event.reason);

  if (
    config.features.enableAnalytics &&
    typeof window !== 'undefined' &&
    window.gtag
  ) {
    window.gtag('event', 'exception', {
      description: `Unhandled promise rejection: ${event.reason}`,
      fatal: false,
    });
  }
});

// Error handling for uncaught errors
window.addEventListener('error', event => {
  console.error('Uncaught error:', event.error);

  if (
    config.features.enableAnalytics &&
    typeof window !== 'undefined' &&
    window.gtag
  ) {
    window.gtag('event', 'exception', {
      description: `Uncaught error: ${event.error?.message || 'Unknown error'}`,
      fatal: false,
    });
  }
});

// Initialize analytics if enabled
if (config.features.enableAnalytics && config.externalServices.analyticsId) {
  // Google Analytics initialization
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${config.externalServices.analyticsId}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;

  gtag('js', new Date());
  gtag('config', config.externalServices.analyticsId, {
    app_name: config.app.name,
    app_version: config.app.version,
    debug_mode: config.environment.isDevelopment,
  });

  console.log('📊 Analytics initialized');
}

// Initialize Sentry for error tracking if configured (temporarily disabled for debugging)
// if (config.externalServices.sentryDsn && !config.environment.isDevelopment) {
//   import('@sentry/react')
//     .then(Sentry => {
//       Sentry.init({
//         dsn: config.externalServices.sentryDsn,
//         environment: config.app.environment,
//         release: config.app.version,
//         integrations: [new Sentry.BrowserTracing()],
//         tracesSampleRate: config.environment.isProduction ? 0.1 : 1.0,
//         beforeSend(event) {
//           // Filter out development errors
//           if (config.environment.isDevelopment) {
//             return null;
//           }
//           return event;
//         },
//       });

//       console.log('🔍 Error tracking initialized');
//     })
//     .catch(error => {
//       console.warn('Failed to initialize error tracking:', error);
//     });
// }

// Get the root element
const container = document.getElementById('root');

if (!container) {
  throw new Error('Root element not found');
}

// Create React root
const root = createRoot(container);

// Render the application
try {
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );

  // Log successful startup
  const loadTime = performance.now() - startTime;
  console.log(
    `✅ Application started successfully in ${loadTime.toFixed(2)}ms`
  );

  // Send performance metrics
  if (
    config.features.enableAnalytics &&
    typeof window !== 'undefined' &&
    window.gtag
  ) {
    window.gtag('event', 'app_startup', {
      startup_time: Math.round(loadTime),
      environment: config.app.environment,
    });
  }
} catch (error) {
  console.error('❌ Failed to render application:', error);

  // Show fallback UI
  container.innerHTML = `
    <div style="
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      font-family: system-ui, sans-serif;
      background: #f3f4f6;
      color: #374151;
      text-align: center;
      padding: 2rem;
    ">
      <div>
        <h1 style="color: #dc2626; margin-bottom: 1rem;">Application Error</h1>
        <p>Failed to start the application. Please refresh the page or contact support.</p>
        <button 
          onclick="window.location.reload()" 
          style="
            margin-top: 1rem;
            padding: 0.5rem 1rem;
            background: #3b82f6;
            color: white;
            border: none;
            border-radius: 0.375rem;
            cursor: pointer;
          "
        >
          Refresh Page
        </button>
      </div>
    </div>
  `;

  throw error;
}

// Web Vitals reporting (temporarily disabled for debugging)
// if (config.features.enableAnalytics) {
//   import('web-vitals')
//     .then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
//       function sendToAnalytics(metric) {
//         if (typeof window !== 'undefined' && window.gtag) {
//           window.gtag('event', metric.name, {
//             value: Math.round(
//               metric.name === 'CLS' ? metric.value * 1000 : metric.value
//             ),
//             event_category: 'Web Vitals',
//             event_label: metric.id,
//             non_interaction: true,
//           });
//         }
//       }

//       getCLS(sendToAnalytics);
//       getFID(sendToAnalytics);
//       getFCP(sendToAnalytics);
//       getLCP(sendToAnalytics);
//       getTTFB(sendToAnalytics);
//     })
//     .catch(error => {
//       console.warn('Failed to load Web Vitals:', error);
//     });
// }

// Development helpers
if (config.environment.isDevelopment) {
  // Make config available in console for debugging
  window.__APP_CONFIG__ = config;

  // Add development tools
  window.__DEV_TOOLS__ = {
    config,
    clearStorage: () => {
      localStorage.clear();
      sessionStorage.clear();
      console.log('🧹 Storage cleared');
    },
    toggleFeature: feature => {
      const currentValue = config.features[feature];
      config.features[feature] = !currentValue;
      console.log(
        `🎛️ Feature '${feature}' toggled: ${currentValue} → ${!currentValue}`
      );
    },
    getPerformanceMetrics: () => {
      if (window.performance && window.performance.getEntriesByType) {
        return {
          navigation: window.performance.getEntriesByType('navigation')[0],
          resources: window.performance.getEntriesByType('resource'),
          measures: window.performance.getEntriesByType('measure'),
        };
      }
      return null;
    },
  };

  console.log('🛠️ Development tools available at window.__DEV_TOOLS__');
}
